// frontend/src/components/public/blocks/GalleryBlock.tsx
import React, { useState } from 'react';
import { Box, Typography, Grid, IconButton, Dialog } from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  OpenInFull as OpenInFullIcon
} from '@mui/icons-material';

interface GalleryBlockProps {
  data: {
    images: Array<{
      url: string;
      caption?: string;
    }>;
    layout: 'grid' | 'masonry' | 'carousel';
    columns: number | string;
    showCaptions: boolean;
    title?: string;
  };
  theme?: {
    primaryColor: string;
    accentColor: string;
  };
}

const GalleryBlock: React.FC<GalleryBlockProps> = ({ data, theme }) => {
  const {
    images = [],
    layout = 'grid',
    columns: columnsStr = '3',
    showCaptions = false,
    title = 'Gallery'
  } = data;

  const columns = parseInt(columnsStr as string, 10) || 3;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');

  const accentColor = theme?.accentColor || '#FF0000';

  if (images.length === 0) {
    return null;
  }

  const handleImageClick = (imageUrl: string) => {
    setLightboxImage(imageUrl);
    setLightboxOpen(true);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Grid Layout
  if (layout === 'grid') {
    return (
      <>
        <Box
          id="gallery"
          sx={{
            py: { xs: 6, md: 10 },
            px: { xs: 3, md: 6 },
            maxWidth: '1400px',
            mx: 'auto'
          }}
        >
          {/* Header */}
          <Typography
            variant="h3"
            sx={{
              mb: 4,
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700,
              color: accentColor
            }}
          >
            {title}
          </Typography>

          <Grid container spacing={3}>
            {images.map((image, index) => (
              <Grid
                item
                xs={12}
                sm={columns === 4 ? 6 : 12 / columns}
                md={12 / columns}
                key={index}
              >
                <Box
                  onClick={() => handleImageClick(image.url)}
                  sx={{
                    position: 'relative',
                    paddingTop: '75%',
                    overflow: 'hidden',
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: `0 8px 24px ${accentColor}33`
                    },
                    '&:hover .image': {
                      transform: 'scale(1.1)'
                    },
                    '&:hover .expand-icon': {
                      opacity: 1
                    }
                  }}
                >
                  <Box
                    className="image"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundImage: `url(${image.url})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      transition: 'transform 0.3s ease'
                    }}
                  />
                  
                  {/* Expansion Icon */}
                  <Box
                    className="expand-icon"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'rgba(0, 0, 0, 0.6)',
                      borderRadius: 1,
                      p: 0.5,
                      opacity: { xs: 1, md: 0 },
                      transition: 'opacity 0.3s ease'
                    }}
                  >
                    <OpenInFullIcon sx={{ color: 'white', fontSize: '1.25rem' }} />
                  </Box>

                  {showCaptions && image.caption && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        bgcolor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        p: 1,
                        fontSize: '0.875rem'
                      }}
                    >
                      {image.caption}
                    </Box>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Lightbox */}
        <Dialog
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          maxWidth={false}
          PaperProps={{
            sx: {
              bgcolor: 'rgba(0, 0, 0, 0.95)',
              boxShadow: 'none',
              maxWidth: '90vw',
              maxHeight: '90vh'
            }
          }}
        >
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' }
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={lightboxImage}
            sx={{
              maxWidth: '100%',
              maxHeight: '90vh',
              objectFit: 'contain'
            }}
          />
        </Dialog>
      </>
    );
  }

  // Masonry Layout
  if (layout === 'masonry') {
    return (
      <>
        <Box
          sx={{
            py: { xs: 6, md: 10 },
            px: { xs: 3, md: 6 },
            maxWidth: '1400px',
            mx: 'auto'
          }}
        >
          {/* Header */}
          <Typography
            variant="h3"
            sx={{
              mb: 4,
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700,
              color: accentColor
            }}
          >
            {title}
          </Typography>

          <Box
            sx={{
              columnCount: { xs: 1, sm: 2, md: columns },
              columnGap: 3
            }}
          >
            {images.map((image, index) => (
              <Box
                key={index}
                onClick={() => handleImageClick(image.url)}
                sx={{
                  breakInside: 'avoid',
                  mb: 3,
                  cursor: 'pointer',
                  borderRadius: 2,
                  overflow: 'hidden',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 32px ${accentColor}33`
                  },
                  '&:hover .image': {
                    transform: 'scale(1.05)'
                  },
                  '&:hover .expand-icon': {
                    opacity: 1
                  }
                }}
              >
                <Box
                  className="image"
                  component="img"
                  src={image.url}
                  sx={{
                    width: '100%',
                    display: 'block',
                    transition: 'transform 0.3s ease'
                  }}
                />
                
                {/* Expansion Icon */}
                <Box
                  className="expand-icon"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'rgba(0, 0, 0, 0.6)',
                    borderRadius: 1,
                    p: 0.5,
                    opacity: { xs: 1, md: 0 },
                    transition: 'opacity 0.3s ease'
                  }}
                >
                  <OpenInFullIcon sx={{ color: 'white', fontSize: '1.25rem' }} />
                </Box>

                {showCaptions && image.caption && (
                  <Typography
                    sx={{
                      mt: 1,
                      fontSize: '0.875rem',
                      color: '#666666'
                    }}
                  >
                    {image.caption}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Lightbox */}
        <Dialog
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          maxWidth={false}
          PaperProps={{
            sx: {
              bgcolor: 'rgba(0, 0, 0, 0.95)',
              boxShadow: 'none',
              maxWidth: '90vw',
              maxHeight: '90vh'
            }
          }}
        >
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' }
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={lightboxImage}
            sx={{
              maxWidth: '100%',
              maxHeight: '90vh',
              objectFit: 'contain'
            }}
          />
        </Dialog>
      </>
    );
  }

  // Carousel Layout
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: '50vh', md: '70vh' },
        bgcolor: '#000'
      }}
    >
      {/* Main Image */}
      <Box
        sx={{
          width: '100%',
          height: '100%',
          backgroundImage: `url(${images[currentIndex].url})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      />

      {/* Caption */}
      {showCaptions && images[currentIndex].caption && (
        <Typography
          sx={{
            position: 'absolute',
            bottom: 60,
            left: '50%',
            transform: 'translateX(-50%)',
            color: 'white',
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            px: 3,
            py: 1.5,
            borderRadius: 1,
            fontSize: '1rem',
            maxWidth: '80%',
            textAlign: 'center'
          }}
        >
          {images[currentIndex].caption}
        </Typography>
      )}

      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          <IconButton
            onClick={handlePrev}
            sx={{
              position: 'absolute',
              left: { xs: 8, md: 24 },
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(0, 0, 0, 0.6)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.8)'
              }
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: '2rem' }} />
          </IconButton>

          <IconButton
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: { xs: 8, md: 24 },
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(0, 0, 0, 0.6)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.8)'
              }
            }}
          >
            <ChevronRightIcon sx={{ fontSize: '2rem' }} />
          </IconButton>
        </>
      )}

      {/* Dots Indicator */}
      {images.length > 1 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1
          }}
        >
          {images.map((_, index) => (
            <Box
              key={index}
              onClick={() => setCurrentIndex(index)}
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: index === currentIndex ? accentColor : 'rgba(255, 255, 255, 0.5)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: index === currentIndex ? accentColor : 'rgba(255, 255, 255, 0.8)'
                }
              }}
            />
          ))}
        </Box>
      )}

      {/* Image Counter */}
      <Typography
        sx={{
          position: 'absolute',
          top: 24,
          right: 24,
          color: 'white',
          bgcolor: 'rgba(0, 0, 0, 0.6)',
          px: 2,
          py: 1,
          borderRadius: 1,
          fontSize: '0.875rem'
        }}
      >
        {currentIndex + 1} / {images.length}
      </Typography>
    </Box>
  );
};

export default GalleryBlock;